/* eslint-disable max-lines, react/jsx-props-no-spreading */
import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import cookie from 'js-cookie'
import styled from '@emotion/styled'
import { DESKTOP_BREAKPOINT, useTheme, Card, useModal } from '@xfers/design-system'
import { datadogRum } from '@datadog/browser-rum'
import PageLoader from '~/components/PageLoader'
import { RouteHooks, PrivateRoute } from '~/components/Route'
import { createGaEvent, createGaPageView } from '~/helpers/gaHelper'
import { MODE, ModeManager } from '~/managers/ModeManager'
import { init, setIsChangeToFiatCurrency } from '~/biz/store/route/actions'
import {
  selectMerchantDetail,
  selectIsKillSwitchActive,
  selectIsDpt2TncAccepted,
  selectUserId,
} from '~/biz/store/merchantInfo/selectors'
import { ConfigManager, DEPLOY_ENVIRONMENT, SITE_TYPE } from '~/managers/ConfigManager'
import {
  setIsXsgdErc20DepositEnabled,
  setIsXsgdErc20WithdrawalEnabled,
  setIsXsgdZrc2DepositEnabled,
  setIsXsgdZrc2WithdrawalEnabled,
  setIsXsgdHtsDepositEnabled,
  setIsXsgdHtsWithdrawalEnabled,
  setIsXsgdMaticDepositEnabled,
  setIsXsgdMaticWithdrawalEnabled,
  setIsHederaUIEnabled,
  setIsPolygonUIEnabled,
  setIsSsoTwoFaAuthenticatorEnabled,
  setIsDptRiskModalEnabled,
  setIsFazzVaForDashboardEnabled,
  setIsTravelRuleDepositDeclarationEnabled,
  setIsTravelRuleAddrWhitelistingEnabled,
  setIsXsgdAvaxDepositEnabled,
  setIsXsgdAvaxWithdrawalEnabled,
  setIsAvalancheUIEnabled,
  setIsBnbsmartUIEnabled,
  setIsTronUIEnabled,
  setIsUsdtTrc20WithdrawalEnabled,
  setIsUsdtTrc20DepositEnabled,
  setIsXusdBep20WithdrawalEnabled,
  setIsXusdBep20DepositEnabled,
  setIsUsdcMaticWithdrawalEnabled,
  setIsUsdcMaticDepositEnabled,
  setIsXsgdArbDepositEnabled,
  setIsXsgdArbWithdrawalEnabled,
  setIsArbitrumUIEnabled,
  setIsRippleUIEnabled,
  setIsXsgdXrpDepositEnabled,
  setIsXsgdXrpWithdrawalEnabled,
  setIsDashboardApprovalFlowUIEnabled,
  setIsUsdtBep20WithdrawalEnabled,
  setIsUsdtBep20DepositEnabled,
  setIsUsdtBep20UIEnabled,
} from '~/biz/store/straitsXLandingPage/actions'
import { getLatestTransactions } from '~/biz/store/straitsXTransactions/actions'
import { RootState } from '~/biz/store/types'
import { straitsXRoutes } from '~/biz/routes/config'
import { selectFeatureMatrixFactors, selectIsFiatWalletOnly } from './store/route/selectors'
import StraitsXFooter from '~/components/StraitsXFooter'
import StraitsXNavigation from '~/components/StraitsXNavigation'
import StraitsXUserInfoBar from '~/components/StraitsXUserInfoBar'
import SandboxBanner from '~/components/SandBoxBanner'
import { Notification } from './store/route/types'
import FeatureGatingManager from '~/managers/FeatureGatingManager'
import { selectDgwMerchantAccount, selectHasBusinessAccount } from './store/accounts/selectors'
import { fetchAccountDetailList, fetchAccountList } from './store/accounts/actions'
import { fetchBankList } from './store/bankAccounts/actions'
import { selectBankAccountListByCurrency } from './store/bankAccounts/selectors'
import { fetchMerchantDetail, fetchUserInfo, fetchUserProfile } from './store/merchantInfo/actions'
import {
  selectBusinessVerificationStatus,
  selectPersonalVerificationStatus,
  selectUserProfile,
  selectUserInfo,
} from './store/merchantInfo/selectors'
import {
  selectBlockchainAddressList,
  selectIsBlockchainAddressesInitiated,
} from './store/blockchainAddresses/selectors'
import {
  fetchBlockchainAddresses,
  setBlockchainAddressList,
  setIsWhitelistingImprovementEnabled,
} from './store/blockchainAddresses/actions'
import { setIsAccountStatementUIEnabled } from './store/straitsXAccountStatement/actions'
import { getTotalTravelRuleComplianceBlockerCount } from './store/straitsXTransactions/actions'
import { useBizFlippers, useBizFlippersByBizId } from '~/store/flippers/hooks'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import {
  selectFlipperWithId,
  selectIsAssetUnificationPhase1Enabled,
  selectIsAssetUnificationPhase1Ready,
  selectIsOtcTrxHistoryEnabled,
} from '~/store/flippers/selectors'
import KillSwitchLockedPage from '~/components/KillSwitchLockedPage'
import Dpt2Tnc from '~/components/Dpt2Tnc'
import { USER_ROLES } from '~/types'
import Dpt2LockedPage from '~/components/Dpt2Tnc/Dpt2LockedPage'
import { PATHS } from './routes/paths'
import { ErrorBoundary } from '~/components/ErrorBoundary/ErrorBoundary'
import { useAutoAddPosthogIds } from '~/hooks/useAutoAddPosthogIds'
import SSOManager from '~/managers/SSOManager'
import { useShield } from '~/hooks/useShield'
import { fetchAssetList } from '~/store/assets/actions'
import { fetchLatestTransactions } from '~/store/transactions/actions'
import { selectUserBizId } from '~/store/route/selectors'

export default () => {
  const dispatch = useDispatch()
  const { resetModal } = useModal()
  const history = useHistory()
  const location = useLocation()
  const {
    isInitiated,
    user,
    notifications: fetchedNotifications,
    isUserInfoInitialised,
    isAccountEnabled,
  } = useSelector((state: RootState) => state.route)
  useAutoAddPosthogIds(SITE_TYPE.BIZ)
  useBizFlippers({ email: user?.email })

  const bizAccount = useSelector(selectDgwMerchantAccount)
  const isBusinessAccount = useSelector(selectHasBusinessAccount)
  const { merchantDetail } = useSelector(selectMerchantDetail)
  const userId = useSelector(selectUserId)
  const bizId = useSelector(selectUserBizId)

  const userProfile = useSelector(selectUserProfile)
  const flipperId = userProfile?.merchantId
  const envMode = ConfigManager.getEnvironmentMode()

  useEffect(() => {
    if (flipperId) {
      FeatureGatingManager.isEnabled('xsgd_use_fiat_currency_name', `Merchant_${flipperId}`).then(
        resp => {
          dispatch(setIsChangeToFiatCurrency(resp))
        }
      )
      FeatureGatingManager.isEnabled(
        [
          'straitsx_xsgd_account_statement_ui',
          'xsgd_erc20_deposit',
          'xsgd_erc20_withdrawal',
          'xsgd_zrc2_deposit',
          'xsgd_zrc2_withdrawal',
          'xsgd_hts_deposit',
          'xsgd_hts_withdrawal',
          'xsgd_matic_deposit',
          'xsgd_matic_withdrawal',
          'xsgd_avax_deposit',
          'xsgd_avax_withdrawal',
          'xsgd_arb_deposit',
          'xsgd_arb_withdrawal',
          'xsgd_xrp_deposit',
          'xsgd_xrp_withdrawal',
          'usdt_tron_deposit',
          'usdt_tron_withdrawal',
          'xsgd_dashboard_hedera_ui',
          'xsgd_dashboard_polygon_ui',
          'xsgd_dashboard_avalanche_ui',
          'xsgd_dashboard_arbitrum_ui',
          'xsgd_dashboard_ripple_ui',
          'usdt_dashboard_tron_ui',
          'usdt_bep20_dashboard_ui',
          'whitelisting_improvement',
          'sso_two_fa_authenticator',
          'straitsx_dpt_risk_modal',
          'straitsx_enable_fazz_va_for_dashboard',
          'straitsx_travel_rule_deposit_declaration',
          'straitsx_travel_rule_addr_whitelisting',
          'straitsx_dashboard_approval_flow_ui',
        ],
        flipperId
      ).then(resp => {
        // testing purpose flippers
        dispatch(setIsAccountStatementUIEnabled(resp.straitsx_xsgd_account_statement_ui))
        dispatch(setIsDptRiskModalEnabled(resp.straitsx_dpt_risk_modal))

        // blockchain related flippers
        dispatch(setIsXsgdErc20DepositEnabled(resp.xsgd_erc20_deposit))
        dispatch(setIsXsgdErc20WithdrawalEnabled(resp.xsgd_erc20_withdrawal))
        dispatch(setIsXsgdZrc2DepositEnabled(resp.xsgd_zrc2_deposit))
        dispatch(setIsXsgdZrc2WithdrawalEnabled(resp.xsgd_zrc2_withdrawal))
        dispatch(setIsXsgdHtsDepositEnabled(resp.xsgd_hts_deposit))
        dispatch(setIsXsgdHtsWithdrawalEnabled(resp.xsgd_hts_withdrawal))
        dispatch(setIsXsgdMaticDepositEnabled(resp.xsgd_matic_deposit))
        dispatch(setIsXsgdMaticWithdrawalEnabled(resp.xsgd_matic_withdrawal))
        dispatch(setIsXsgdAvaxDepositEnabled(resp.xsgd_avax_deposit))
        dispatch(setIsXsgdAvaxWithdrawalEnabled(resp.xsgd_avax_withdrawal))
        dispatch(setIsXsgdArbDepositEnabled(resp.xsgd_arb_deposit))
        dispatch(setIsXsgdArbWithdrawalEnabled(resp.xsgd_avax_withdrawal))
        dispatch(setIsXsgdXrpDepositEnabled(resp.xsgd_xrp_deposit))
        dispatch(setIsXsgdXrpWithdrawalEnabled(resp.xsgd_xrp_withdrawal))
        dispatch(setIsUsdtTrc20DepositEnabled(resp.usdt_tron_deposit))
        dispatch(setIsUsdtTrc20WithdrawalEnabled(resp.usdt_tron_withdrawal))
        dispatch(setIsHederaUIEnabled(resp.xsgd_dashboard_hedera_ui))
        dispatch(setIsPolygonUIEnabled(resp.xsgd_dashboard_polygon_ui))
        dispatch(setIsAvalancheUIEnabled(resp.xsgd_dashboard_avalanche_ui))
        dispatch(setIsTronUIEnabled(resp.usdt_dashboard_tron_ui))
        dispatch(setIsArbitrumUIEnabled(resp.xsgd_dashboard_arbitrum_ui))
        dispatch(setIsRippleUIEnabled(resp.xsgd_dashboard_ripple_ui))
        dispatch(setIsWhitelistingImprovementEnabled(resp.whitelisting_improvement))
        dispatch(setIsSsoTwoFaAuthenticatorEnabled(resp.sso_two_fa_authenticator))
        dispatch(setIsFazzVaForDashboardEnabled(resp.straitsx_enable_fazz_va_for_dashboard))
        dispatch(
          setIsTravelRuleDepositDeclarationEnabled(resp.straitsx_travel_rule_deposit_declaration)
        )
        dispatch(
          setIsTravelRuleAddrWhitelistingEnabled(resp.straitsx_travel_rule_addr_whitelisting)
        )
        dispatch(setIsDashboardApprovalFlowUIEnabled(resp.straitsx_dashboard_approval_flow_ui))
      })
    }
  }, [dispatch, flipperId])

  useShield(userId)

  useEffect(() => {
    if (user?.email) {
      FeatureGatingManager.isEnabled(
        [
          'xusd_dashboard_bnbsmart_biz_ui',
          'xusd_bep20_deposit_biz',
          'xusd_bep20_withdrawal_biz',
          'usdc_matic_deposit',
          'usdc_matic_withdrawal',
          'usdt_bep20_deposit',
          'usdt_bep20_withdrawal',
          'usdt_bep20_dashboard_ui',
        ],
        user?.email
      ).then(resp => {
        dispatch(setIsBnbsmartUIEnabled(resp.xusd_dashboard_bnbsmart_biz_ui))
        dispatch(setIsXusdBep20DepositEnabled(resp.xusd_bep20_deposit_biz))
        dispatch(setIsXusdBep20WithdrawalEnabled(resp.xusd_bep20_withdrawal_biz))
        dispatch(setIsUsdcMaticDepositEnabled(resp.usdc_matic_deposit))
        dispatch(setIsUsdcMaticWithdrawalEnabled(resp.usdc_matic_withdrawal))
        dispatch(setIsUsdtBep20DepositEnabled(resp.usdt_bep20_deposit))
        dispatch(setIsUsdtBep20WithdrawalEnabled(resp.usdt_bep20_withdrawal))
        dispatch(setIsUsdtBep20UIEnabled(resp.usdt_bep20_dashboard_ui))
      })
    }
  }, [user?.email])
  useBizFlippersByBizId(bizId)

  const verificationStatus = useSelector(selectPersonalVerificationStatus)
  const businessVerificationStatus = useSelector(selectBusinessVerificationStatus)
  const { bankAccountList: bankAccounts, isBankAccountListInitiated } = useSelector(
    selectBankAccountListByCurrency
  )()

  const blockchainAddresses = useSelector(selectBlockchainAddressList)
  const isBlockchainAddressListInitiated = useSelector(selectIsBlockchainAddressesInitiated)

  const {
    isDeveloperToolEnabled: displayDevToolTab,
    isSandboxEnabled: displayModeSwitcher,
    role,
  } = useSelector(selectFeatureMatrixFactors)

  const { isAccountStatementUIEnabled } = useSelector(
    (state: RootState) => state.straitsXAccountStatement
  )

  const isKillSwitchActive = useSelector(selectIsKillSwitchActive)

  const isDpt2TncAccepted = useSelector(selectIsDpt2TncAccepted)

  const [displayGetStartedTab, setDisplayGetStartedTab] = useState(false)

  const { isAccountListInitiated } = useSelector((state: RootState) => state.accounts)
  const { userInfo } = useSelector(selectUserInfo)
  const cardsDashboardEnabled = userInfo?.cardsDashboardEnabled || false
  const straitsxMenuDisabled = userInfo?.straitsxMenuDisabled || false

  const isOtcTrxHistoryEnabled = useSelector(selectIsOtcTrxHistoryEnabled)

  const flipperDisableManageAccountsPage = useSelector(
    selectFlipperWithId(FLIPPER_KEYS.STRAITSX_SANDBOX_DISABLE_MANAGE_ACCOUNTS_PAGE, user?.email)
  )

  const flipperDpt2Tnc = useSelector(selectFlipperWithId(FLIPPER_KEYS.DPT2_TNC, user?.email))

  // TODO(benedictkhoo): remove after assets unification is done
  const isAssetUnificationPhase1Ready = useSelector(selectIsAssetUnificationPhase1Ready)
  const isAssetUnificationPhase1Enabled = useSelector(selectIsAssetUnificationPhase1Enabled)

  const isFiatWalletOnly = useSelector(selectIsFiatWalletOnly)

  // fetching application needed data
  useEffect(() => {
    dispatch(fetchUserInfo())
    dispatch(fetchBankList())
    dispatch(fetchMerchantDetail())
    dispatch(fetchUserProfile())
    dispatch(getTotalTravelRuleComplianceBlockerCount())
  }, [])

  useEffect(() => {
    if (!isAssetUnificationPhase1Ready) {
      return
    }

    if (isAssetUnificationPhase1Enabled) {
      dispatch(fetchAssetList())
    } else {
      // TODO(benedictkhoo): remove after assets unification is done
      dispatch(fetchAccountList())
      dispatch(fetchAccountDetailList())
    }
  }, [isAssetUnificationPhase1Ready, isAssetUnificationPhase1Enabled])

  useEffect(() => {
    if (!isAccountListInitiated || !isOtcTrxHistoryEnabled) {
      return
    }

    if (isAssetUnificationPhase1Enabled) {
      dispatch(fetchLatestTransactions())
    } else {
      // TODO(benedictkhoo): remove after assets unification is done
      dispatch(getLatestTransactions())
    }
  }, [isAccountListInitiated, isOtcTrxHistoryEnabled, isAssetUnificationPhase1Enabled])

  useEffect(() => {
    if (!isAccountListInitiated) return

    if (bizAccount?.accountId) {
      dispatch(fetchBlockchainAddresses(bizAccount.accountId))
    } else {
      dispatch(setBlockchainAddressList([]))
    }
  }, [dispatch, isAccountListInitiated, bizAccount?.accountId])

  // on sandbox<->production mode switched
  const onModeSwitched = () => {
    // TODO: think about better solution
    window.location.reload()
  }

  useEffect(() => {
    if (!isAccountEnabled) {
      cookie.remove('SESSIONID')
      SSOManager.ssoLogout()
    }
  }, [isAccountEnabled])

  const isSandbox = ModeManager.isSandboxMode()
  useEffect(() => {
    const isVerificationPending =
      businessVerificationStatus && businessVerificationStatus !== 'verified'

    if (isSandbox) {
      setDisplayGetStartedTab(false)
      return
    }

    if (isVerificationPending && role === USER_ROLES.OWNER) {
      setDisplayGetStartedTab(true)
    }
  }, [isSandbox, businessVerificationStatus, role])

  history.listen(loc => {
    const urlPath = loc.pathname + loc.search
    createGaPageView(urlPath)
    createGaEvent('URL', urlPath, 'Changed URL')
  })

  useEffect(() => {
    if (!isInitiated) dispatch(init())
  }, [isInitiated]) // Run init just once

  const notifications = useMemo(
    () => ({
      isLoading: fetchedNotifications.isLoading,
      data: fetchedNotifications.data,
      unreadCount: fetchedNotifications.unreadCount,
    }),
    [fetchedNotifications.isLoading]
  )

  useEffect(() => {
    if (ConfigManager.isProd() && userId) {
      datadogRum.setUser({
        straitsx_user_id: userId,
      })
    }

    if (envMode === DEPLOY_ENVIRONMENT.PRODUCTION && userId && user?.kcId) {
      posthog.identify(posthog.get_distinct_id(), { straitsx_user_id: userId, kcId: user?.kcId })
    }
  }, [userId, user])

  const displayEarnTab = !isFiatWalletOnly && !!userProfile?.merchantId && !isSandbox

  const memoizedNav = useMemo(() => {
    return (
      <StraitsXNavigation
        displayEarnTab={displayEarnTab}
        displayGetStartedTab={displayGetStartedTab}
        displayDevToolTab={displayDevToolTab}
        displayAccountStatementTab={isAccountStatementUIEnabled}
        displayUserCardsTab={cardsDashboardEnabled}
        disableStraitsxMenu={straitsxMenuDisabled}
        isSandbox={isSandbox}
        isBusinessAccount={isBusinessAccount}
      />
    )
  }, [
    isSandbox,
    displayGetStartedTab,
    isBusinessAccount,
    displayDevToolTab,
    isAccountStatementUIEnabled,
    isFiatWalletOnly,
    userProfile?.merchantId,
  ])

  if (!isInitiated) {
    return <PageLoader />
  }

  if (isKillSwitchActive) {
    return <KillSwitchLockedPage />
  }

  if (
    !isDpt2TncAccepted &&
    isUserInfoInitialised &&
    !isKillSwitchActive &&
    flipperDpt2Tnc?.enabled &&
    !isSandbox
  ) {
    if (role === USER_ROLES.OWNER) {
      return <Dpt2Tnc siteType={SITE_TYPE.BIZ} />
    }
    return <Dpt2LockedPage />
  }

  return (
    <Root id="biz-root">
      <RouteHooks resetModal={resetModal} />

      {isKillSwitchActive !== undefined && (
        <Container>
          <NavigationWrapper>{memoizedNav}</NavigationWrapper>
          <MainContainer isSandbox={isSandbox}>
            <ErrorBoundary key={location.pathname}>
              {isSandbox && <SandboxBanner />}
              <StraitsXUserInfoBar
                userInfo={{
                  fullName: userProfile?.fullName || '',
                  avatarUrl: userProfile?.avatarUrl || '',
                  displayname: merchantDetail?.businessName || '',
                }}
                isSandbox={isSandbox}
                notifications={notifications}
                displayGetStartedTab={displayGetStartedTab}
                displayDevToolTab={displayDevToolTab}
                displayAccountStatementTab={isAccountStatementUIEnabled}
                displayEarnTab={displayEarnTab}
                displayModeSwitcher={displayModeSwitcher}
                displayUserCardsTab={cardsDashboardEnabled}
                disableStraitsxMenu={straitsxMenuDisabled}
                isBusinessAccount={isBusinessAccount}
                onChangeModeCallback={onModeSwitched}
              />

              <Suspense fallback={<LoadingPage />}>
                <Switch>
                  {cardsDashboardEnabled && straitsxMenuDisabled && (
                    <Route exact path={PATHS.LANDING_PAGE}>
                      <Redirect to={PATHS.USER_CARDS} />
                    </Route>
                  )}
                  <Route path={PATHS.VERIFICATION}>
                    <Redirect to={PATHS.LANDING_PAGE} />
                  </Route>
                  {envMode === MODE.SANDBOX.toString() &&
                    flipperDisableManageAccountsPage?.enabled && (
                      <Route path={PATHS.MANAGE_ACCOUNT}>
                        <Redirect to={PATHS.LANDING_PAGE} />
                      </Route>
                    )}
                  {straitsXRoutes.map((route, i) => (
                    <PrivateRoute key={i} {...route} />
                  ))}
                </Switch>
              </Suspense>

              <StraitsXFooter />
            </ErrorBoundary>
          </MainContainer>
        </Container>
      )}
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card style={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;
  background: none;

  ${DESKTOP_BREAKPOINT} {
    background-color: #f6f7f9;
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-size: 100%;
  background-color: #f6f8f9;
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 73px;
  width: 100%;

  ${DESKTOP_BREAKPOINT} {
    padding-top: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '73px' : 0)};
  }
`

const NavigationWrapper = styled.div`
  z-index: 1;
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  float: left;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(90deg, #e8e8e8 0%, #f6f7f9 50%, #e8e8e8 100%);
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
